import { lazy, Suspense, useEffect, useState } from 'react'
import { CitationSource } from 'types/chat-message'
import { getSignedUrl } from 'api/uploads'

type ChartCitationProps = {
  source?: CitationSource
}

// ! Highcharts is ~300KB: pulled in only by messages that actually carry a chart.
const HighchartsChart = lazy(async () => {
  const [{ default: Highcharts }, { default: HighchartsReact }] = await Promise.all([
    import('highcharts'),
    import('highcharts-react-official'),
  ])

  return {
    default: ({ options }: { options: Record<string, unknown> }) => (
      <HighchartsReact highcharts={Highcharts} options={options} />
    ),
  }
})

// data-slot="chart-citation" (skeleton included, so it is there before Highcharts measures): the message
// bubble, width-fit by default, goes full width when it holds a chart instead of shrinking it to the text.
const Skeleton = () => (
  <span data-slot="chart-citation" className="my-2 block h-[260px] w-full animate-pulse rounded-lg bg-muted" />
)

/**
 * Inline chart citation ([chart:N]). The chart tool stores the Highcharts config as a private json
 * upload linked to the chat: it is signed and fetched here, so the message never carries the points.
 */
export const ChartCitation = ({ source }: ChartCitationProps) => {
  const [options, setOptions] = useState<Record<string, unknown> | null>(null)
  const [failed, setFailed] = useState(false)
  const url = source?.url

  useEffect(() => {
    if (!url) return

    let cancelled = false

    getSignedUrl(url)
      .then((signed) => fetch(signed))
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status))
        return response.json() as Promise<Record<string, unknown>>
      })
      .then((config) => {
        if (cancelled) return
        setOptions({
          ...config,
          credits: { enabled: false },
          time: { useUTC: false },
          chart: { ...(config.chart as object), height: 300 },
        })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [url])

  if (!url || failed) {
    return <span className="my-2 block text-xs text-muted-foreground">{source?.name}</span>
  }

  if (!options) return <Skeleton />

  // ! span, not div: the marker sits inside the markdown <p>
  return (
    <span data-slot="chart-citation" className="my-2 block w-full overflow-hidden rounded-lg border border-border">
      <Suspense fallback={<Skeleton />}>
        <HighchartsChart options={options} />
      </Suspense>
    </span>
  )
}
